import { useState, useRef, useEffect, KeyboardEvent } from 'react'
import {
  PaperPlaneRight,
  Paperclip,
  X,
  File as FileIcon,
  FilePdf,
  Image as ImageIcon,
  Spinner,
  Microphone,
  Stop,
  Waveform,
  Trash
} from '@phosphor-icons/react'

interface MessageInputProps {
  onSendMessage: (content: string) => void
  onSendAttachment: (file: File, caption?: string) => void
  disabled?: boolean
  sending?: boolean
}

const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/zip',
  'application/x-rar-compressed',
  'audio/webm',
  'audio/mp4',
  'audio/mpeg',
  'audio/ogg',
]

const MAX_SIZE_PDF = 20 * 1024 * 1024 // 20MB
const MAX_SIZE_DEFAULT = 10 * 1024 * 1024 // 10MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) {
    return <ImageIcon size={20} className="text-blue-500" />
  }
  if (mimeType === 'application/pdf') {
    return <FilePdf size={20} className="text-red-500" />
  }
  return <FileIcon size={20} className="text-gray-500" />
}

export function MessageInput({
  onSendMessage,
  onSendAttachment,
  disabled,
  sending
}: MessageInputProps) {
  const [message, setMessage] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [filePreview, setFilePreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Audio recording state
  const [isRecording, setIsRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const recordingIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Format recording time
  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      })

      audioChunksRef.current = []
      mediaRecorderRef.current = mediaRecorder

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType })
        setAudioBlob(audioBlob)
        setAudioUrl(URL.createObjectURL(audioBlob))

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop())
      }

      mediaRecorder.start()
      setIsRecording(true)
      setRecordingTime(0)

      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1)
      }, 1000)
    } catch (err) {
      console.error('Error accessing microphone:', err)
      setFileError('Não foi possível acessar o microfone. Verifique as permissões.')
    }
  }

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)

      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current)
        recordingIntervalRef.current = null
      }
    }
  }

  // Cancel recording
  const cancelRecording = () => {
    stopRecording()
    setAudioBlob(null)
    setAudioUrl(null)
    setRecordingTime(0)
  }

  // Send audio
  const sendAudio = () => {
    if (audioBlob) {
      const extension = audioBlob.type.includes('webm') ? 'webm' : 'm4a'
      const audioFile = new File([audioBlob], `audio_${Date.now()}.${extension}`, { type: audioBlob.type })
      onSendAttachment(audioFile)
      cancelRecording()
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current)
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl)
      }
    }
  }, [audioUrl])

  const handleSend = () => {
    if (sending || disabled) return

    if (selectedFile) {
      onSendAttachment(selectedFile, message.trim() || undefined)
      setSelectedFile(null)
      setFilePreview(null)
      setMessage('')
    } else if (message.trim()) {
      onSendMessage(message.trim())
      setMessage('')
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    // Validate type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setFileError('Tipo de arquivo nao permitido. Use: imagens, PDF, DOC, XLS ou ZIP')
      return
    }

    // Validate size
    const maxSize = file.type === 'application/pdf' ? MAX_SIZE_PDF : MAX_SIZE_DEFAULT
    if (file.size > maxSize) {
      const maxSizeMB = maxSize / (1024 * 1024)
      setFileError(`Arquivo muito grande. Tamanho maximo: ${maxSizeMB}MB`)
      return
    }

    setFileError(null)
    setSelectedFile(file)

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (e) => {
        setFilePreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    } else {
      setFilePreview(null)
    }
  }

  const removeFile = () => {
    setSelectedFile(null)
    setFilePreview(null)
    setFileError(null)
  }

  return (
    <div className="border-t border-gray-200 bg-white p-4">
      {/* File error */}
      {fileError && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center justify-between">
          <span>{fileError}</span>
          <button onClick={() => setFileError(null)} className="p-1 hover:bg-red-100 rounded">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Selected file preview */}
      {selectedFile && (
        <div className="mb-3 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            {filePreview ? (
              <img
                src={filePreview}
                alt="Preview"
                className="w-16 h-16 rounded-lg object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
                {getFileIcon(selectedFile.type)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700 truncate">
                {selectedFile.name}
              </p>
              <p className="text-xs text-gray-500">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
            <button
              onClick={removeFile}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded-full transition-colors"
              title="Remover arquivo"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Audio recording UI */}
      {(isRecording || audioBlob) && (
        <div className="mb-3 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            {/* Recording indicator / Audio preview */}
            <div className="flex-1 flex items-center gap-3">
              {isRecording ? (
                <>
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <Waveform size={24} className="text-emerald-500 animate-pulse" weight="bold" />
                  <span className="text-sm font-medium text-gray-700">
                    Gravando... {formatRecordingTime(recordingTime)}
                  </span>
                </>
              ) : audioUrl && (
                <>
                  <Waveform size={24} className="text-emerald-500" weight="bold" />
                  <audio src={audioUrl} controls className="h-10 flex-1" />
                  <span className="text-xs text-gray-500">{formatRecordingTime(recordingTime)}</span>
                </>
              )}
            </div>

            {/* Recording controls */}
            <div className="flex items-center gap-2">
              {isRecording ? (
                <button
                  onClick={stopRecording}
                  className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  title="Parar gravação"
                >
                  <Stop size={20} weight="fill" />
                </button>
              ) : (
                <>
                  <button
                    onClick={cancelRecording}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded-full transition-colors"
                    title="Descartar áudio"
                  >
                    <Trash size={20} />
                  </button>
                  <button
                    onClick={sendAudio}
                    disabled={sending}
                    className="p-2 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 transition-colors disabled:bg-gray-300"
                    title="Enviar áudio"
                  >
                    {sending ? (
                      <Spinner size={20} className="animate-spin" />
                    ) : (
                      <PaperPlaneRight size={20} weight="fill" />
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Input area */}
      {!isRecording && !audioBlob && (
        <div className="flex items-end gap-2">
          {/* Attachment button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || sending}
            className="p-2.5 text-gray-400 hover:text-emerald-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            title="Anexar arquivo"
          >
            <Paperclip size={22} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            accept={ALLOWED_TYPES.join(',')}
          />

          {/* Text input */}
          <div className="flex-1 relative">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={selectedFile ? "Adicione uma legenda (opcional)..." : "Digite sua mensagem..."}
              disabled={disabled || sending}
              rows={1}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400 max-h-32 overflow-y-auto"
              style={{ minHeight: '42px' }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement
                target.style.height = 'auto'
                target.style.height = Math.min(target.scrollHeight, 128) + 'px'
              }}
            />
          </div>

          {/* Microphone button (when no text/file) */}
          {!message.trim() && !selectedFile ? (
            <button
              onClick={startRecording}
              disabled={disabled || sending}
              className="p-2.5 text-gray-400 hover:text-emerald-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
              title="Gravar áudio"
            >
              <Microphone size={22} />
            </button>
          ) : null}

          {/* Send button */}
          <button
            onClick={handleSend}
            disabled={disabled || sending || (!message.trim() && !selectedFile)}
            className="p-2.5 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            title="Enviar"
          >
            {sending ? (
              <Spinner size={22} className="animate-spin" />
            ) : (
              <PaperPlaneRight size={22} weight="fill" />
            )}
          </button>
        </div>
      )}

      {/* Hint */}
      {!isRecording && !audioBlob && (
        <p className="mt-2 text-xs text-gray-400 text-center">
          Pressione Enter para enviar, Shift+Enter para nova linha
        </p>
      )}
    </div>
  )
}
